import { useState, useCallback } from 'react';
import { Loader2, Eye, CheckCircle2, GitMerge } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function AccountDedupePanel() {
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);

  const runPreview = useCallback(async () => {
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.accountDedupePreview();
      setPreview(data);
      if (data.duplicate_groups === 0) {
        toast.success('No duplicate connected accounts found');
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }, []);

  const runApply = useCallback(async () => {
    setApplying(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.accountDedupeApply();
      setResult(data);
      toast.success(`Merged ${data.accounts_merged} duplicate account(s)`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <GitMerge className="h-5 w-5" />
          Connected Account Dedupe
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          EnableBanking can mint a new account doc per reconnect, so one real account may appear as
          several docs. This merges only PROVABLY-same accounts into one survivor: an exact IBAN
          match, or a stable provider-account id that survives reconnection. There is no name+currency
          fallback (multi-currency pockets under one holder name are distinct accounts). Transactions,
          subscriptions and matches follow the survivor; losers are marked merged_into and hidden,
          never deleted. Preview lists every merge pair, and why it qualifies, before you apply.
        </p>

        <div className="flex items-end gap-3 flex-wrap">
          <Button onClick={runPreview} disabled={previewing || applying} variant="outline">
            {previewing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>
          <Button
            onClick={runApply}
            disabled={!preview || applying || previewing || preview.duplicate_groups === 0}
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Apply
          </Button>
        </div>

        {preview && (
          <div className="rounded-lg border border-blue-200 dark:border-blue-800 p-3 bg-blue-50/50 dark:bg-blue-950/20">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium">Preview</span>
              <Badge variant="secondary" className="text-xs">
                {preview.duplicate_groups} group(s), {preview.accounts_to_merge} to merge
              </Badge>
            </div>

            {preview.pairs && preview.pairs.length > 0 && (
              <div className="space-y-3 max-h-80 overflow-auto">
                {preview.pairs.map((p) => (
                  <div key={p.merge_key} className="rounded border bg-background p-2 text-xs">
                    {p.qualification && (
                      <div className="mb-1 text-[10px] text-muted-foreground">
                        qualifies: {p.qualification}
                      </div>
                    )}
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 text-[10px]">
                        survivor
                      </Badge>
                      <span className="font-medium">{p.survivor.account_name}</span>
                      <span className="text-muted-foreground">
                        {p.survivor.currency} · {p.survivor.transaction_count} tx · {p.survivor.id}
                      </span>
                    </div>
                    {p.losers.map((l) => (
                      <div key={l.id} className="flex items-center gap-2 pl-4 text-muted-foreground">
                        <Badge variant="outline" className="text-[10px]">merge</Badge>
                        <span>{l.account_name}</span>
                        <span>
                          {l.currency} · {l.transaction_count} tx · {l.id}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {result && (
          <div className="rounded-lg border border-green-200 dark:border-green-800 p-3 bg-green-50/50 dark:bg-green-950/20">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium">Applied</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <span className="text-muted-foreground">Accounts merged:</span>{' '}
                <span className="font-medium text-green-600">{result.accounts_merged}</span>
              </div>
              {result.refs_reassigned &&
                Object.entries(result.refs_reassigned).map(([coll, n]) => (
                  <div key={coll}>
                    <span className="text-muted-foreground">{coll}:</span>{' '}
                    <span className="font-medium">{n}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
